"use client";

import { useSearchParams } from "next/navigation";
import React, { ReactElement, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import HelpOverlayButton from "@/app/shared-components/help-overlay-button/help-overlay-button-wizard";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { type BtaVotingContentProps } from "@/components/molecules/confirmation-section/bta-voting-content";
import Logo from "@/components/molecules/logo-component/logo";
import { RenewalPage } from "@/config/app-constant";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useGtmViewQuoteData from "@/hooks/gtm/use-gtm-view-quote";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import RenewalStateProvider from "@/providers/renewal-state-provider";
import { RootState } from "@/store/root-reducer";
import QuoteDataMap from "@/utils/gtm/quote-data-map";

import InterstitialPageContent from "../organisms/renewals/interstitial/interstitial-loading-page-conetent";
import LandinPageContent from "../organisms/renewals/landing/page-content";
import withQuoteData from "../with-quote-data";

interface RenwalWrapperProps {
  renewalsHeaderBanner: ReactElement;
  leftPanel: ReactElement;
  middlePanel: ReactElement;
  rightPanel: ReactElement;
  footer: ReactElement;
  btaVotingData?: Omit<BtaVotingContentProps, "variant">;
  loaderData?: {
    title: string;
    mobileContent: string[];
    desktopContent: string[];
  };
  isLoading?: boolean;
}

function RenewalsPageLayoutWrapper({
  renewalsHeaderBanner,
  leftPanel,
  middlePanel,
  rightPanel,
  btaVotingData,
  loaderData,
  footer,
}: Readonly<RenwalWrapperProps>) {
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");
  const [loading, setLoading] = React.useState(true);
  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );
  const { current } = useWizard();

  const searchParams = useSearchParams();

  useEffect(() => {
    const hasReference = Boolean(referenceId?.trim());

    if (hasReference) return;

    const navEntry = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;

    const quoteId = searchParams.get("quoteId");
    const affiliate = searchParams.get("affiliate");

    const shouldClear =
      (navEntry?.type === "navigate" && quoteId && affiliate) ||
      navEntry?.type === "back_forward";

    if (shouldClear) {
      sessionStorage.clear();
    }
  }, [referenceId, searchParams]);

  useUpdateProgressBar(current?.progressBarPercentage ?? -1);

  const { pageReferrer, pageUrl } = useCustomPageTracking(RenewalPage);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle: "Dashboard",
  });

  const isLoadingData = useMemo(() => {
    if (!loading && referenceId) {
      sessionStorage.setItem("isSession", JSON.stringify(true));
      sessionStorage.removeItem("isMedicalAccordionOpen");
    }
    return !referenceId;
  }, [referenceId, loading]);

  let gtmQuoteViewData = null;

  if (referenceId) {
    gtmQuoteViewData = QuoteDataMap(
      attributes,
      "view_renewal_quote",
      "renewal_quote",
      referenceId,
      "renewal_journey"
    );
  }

  useGtmViewQuoteData({
    quoteData: gtmQuoteViewData,
    isReady: !isLoadingData,
  });

  if (!isReady) return null;

  return (
    <div
      data-testid="renewals-page-test-id"
      className="bg-surface-neutral-option-2 "
    >
      <div className="max-w-[1200px] mx-auto my-0 w-[100%] relative min-h-[calc(100vh-12px)] md:pb-0 pb-spacing-5xl ">
        <header className="md:pt-spacing-xl lg:py-spacing-xxl md:pb-spacing-l pt-spacing-m pb-spacing-m">
          <div
            className=" md:gap-spacing-l gap-spacing-m xl:mx-auto lg:mx-[72px] 
      md:mx-[48px] mx-[24px] relative"
          >
            <div className="bg-dark-surface-accent-1-primary rounded-radius-xxl">
              <div className="flex flex-col h-full ">
                <div className="text-surface-neutral-option-1 relative">
                  <HelpOverlayButton />
                  <Logo
                    color="white"
                    heightClass="h-[80px]"
                    size="xl"
                    showondesktop={isdesktop.toString()}
                  />
                </div>
                {sessionStorage.getItem("isSession") !== "true" ? (
                  <InterstitialPageContent
                    listItemContent={loaderData}
                    setComplete={(val) => {
                      setLoading(!val);
                    }}
                  />
                ) : (
                  renewalsHeaderBanner
                )}
              </div>
            </div>
          </div>
        </header>
        {sessionStorage.getItem("isSession") === "true" && (
          /**
           * RenewalStateProvider
           * wrapper provider to share selected section
           *  */
          <RenewalStateProvider isLoading={isLoadingData}>
            <LandinPageContent
              leftPanel={leftPanel}
              middlePanel={middlePanel}
              rightPanel={rightPanel}
              btaVotingData={btaVotingData}
              footer={footer}
            />
          </RenewalStateProvider>
        )}
      </div>
    </div>
  );
}

export default withQuoteData(RenewalsPageLayoutWrapper, "", RenewalPage);
